import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import DialogComponent from '../../component/dialog/dialog.component';
import DialogFormContentSurface, { getDialogFormBodyContentSx } from '../../component/dialog/dialog-form-content-surface.component';
import GlobalNotification from '../../component/common/snackbar';
import BANNER_ANNOUNCEMENT from '../../static/banner-announcement-data';
import { apiManage_rbp } from '../../request';
import { BannerAnnouncement } from '../../models';
import _ from 'lodash';
import './scss/banner-announcement.scss';
import i18n from '../../i18n';
import BaseInput from '../../component/form/base-input.component';
import { CardToolbarIconButton } from '../../component/button/card-toolbar-icon-button.component';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import { SubFunctionAuxiliaryHint } from '../rbp-cloud-sub-function/sub-function-auxiliary-hint.component';

/**
 * 顶部文字公告：云 RBP 首屏按 Figma 1838:52035 使用 Flex 列表（无表格分割线），新增/编辑仍走弹窗表单。
 */
export default class BannerAnnouncementComponent extends React.Component<Props, State> {
    /** 当前公告列表，由接口拉取 */
    public state: Readonly<State> = {
        bannerAnnouncement: [],
        open: false,
        /** 弹窗「确定」提交中，与公共 LoadingOverlayButton 同步 */
        saveLoading: false,
        /** 删除顶部文字公告二次确认弹窗是否打开 */
        deleteConfirmOpen: false,
        /** 待删除行的 id，确认后调删除接口 */
        deleteTargetId: null,
        /** 待删除公告内容摘要，用于弹窗第二行高亮 */
        deleteHighlight: '',
        /** 删除请求进行中，AlertDialog 确认按钮 loading、禁止关闭 */
        deleteLoading: false,
    };

    componentDidMount() {
        void this.init();
    }

    /** 拉取顶部文字公告列表 */
    async init() {
        await this.getBannerAnnouncement();
    }

    async getBannerAnnouncement() {
        const bannerAnnouncement = await apiManage_rbp.get_banner_announcement();
        if (!_.get(bannerAnnouncement, 'error')) {
            this.setState({
                bannerAnnouncement,
            });
        }
    }

    /** 弹窗内保存：校验通过后请求接口，期间展示保存按钮 loading（与社媒弹窗一致） */
    async saveBannerAnnouncement() {
        const { bannerAnnouncementDetail = {} } = this.state;
        if (!(bannerAnnouncementDetail.icon && bannerAnnouncementDetail.value)) {
            GlobalNotification.error(i18n.t('dis_setting_page_type_entire_info'));
            return;
        }
        this.setState({ saveLoading: true });
        try {
            let result;
            if (typeof bannerAnnouncementDetail.id === 'undefined') {
                result = await apiManage_rbp.post_banner_announcement(bannerAnnouncementDetail);
            } else {
                const { id, ...otherParams } = bannerAnnouncementDetail;
                result = await apiManage_rbp.patch_banner_announcement(id, otherParams);
            }

            if (!_.get(result, 'error')) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                this.setState({
                    open: false,
                });
                void this.getBannerAnnouncement();
            } else {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            }
        } finally {
            this.setState({ saveLoading: false });
        }
    }

    /**
     * 关闭删除确认弹窗（请求中不可关，由 confirmLoading 与组件行为保证）。
     */
    private closeDeleteConfirm = (): void => {
        if (this.state.deleteLoading) {
            return;
        }
        this.setState({
            deleteConfirmOpen: false,
            deleteTargetId: null,
            deleteHighlight: '',
        });
    };

    /**
     * 用户确认后删除一条顶部文字公告；接口可能较慢，期间保持确认按钮 loading。
     */
    private handleConfirmDeleteBanner = async (): Promise<void> => {
        const { deleteTargetId } = this.state;
        if (deleteTargetId == null) {
            return;
        }
        this.setState({ deleteLoading: true });
        try {
            const result = await apiManage_rbp.delete_banner_announcement(deleteTargetId);
            if (!_.get(result, 'error')) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                this.setState({
                    deleteConfirmOpen: false,
                    deleteTargetId: null,
                    deleteHighlight: '',
                    bannerAnnouncementDetail: undefined,
                });
                void this.getBannerAnnouncement();
            } else {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            }
        } finally {
            this.setState({ deleteLoading: false });
        }
    };

    onChangeBannerAnnouncement(value: string, key: string) {
        const { bannerAnnouncementDetail = {} } = this.state;
        _.set(bannerAnnouncementDetail, key, value);
        this.setState({
            bannerAnnouncementDetail,
        });
    }

    render() {
        const {
            bannerAnnouncement,
            bannerAnnouncementDetail,
            open,
            saveLoading,
            deleteConfirmOpen,
            deleteHighlight,
            deleteLoading,
        } = this.state;

        const bannerAnnouncementForm = [
            {
                key: 'icon',
                label: i18n.t('first_screen_setting_page_icon'),
                regularType: 'required',
                require: true,
                select: true,
                items: BANNER_ANNOUNCEMENT.map((item) => ({
                    value: item.key,
                    label: (<span className="banner-announcement-option-icon">{item?.icon}</span>) as any,
                })),
            },
            {
                key: 'value',
                label: i18n.t('first_screen_setting_page_notice_content'),
                regularType: 'required',
                require: true,
            },
        ];

        const auxiliaryModeEnabled = this.props.auxiliaryModeEnabled === true;

        return (
            <Box
                className="banner-announcement-root"
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                }}
            >
                <Box
                    className="banner-announcement-layout"
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        width: '100%',
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                        <Typography variant="subtitle1" sx={{ color: 'var(--text-primary-300)', fontWeight: 700 }}>
                            {i18n.t('first_screen_setting_page_top_text_notify')}
                        </Typography>
                        <CardToolbarIconButton
                            kind="add"
                            ariaLabel={i18n.t('general_text_add')}
                            onClick={() => {
                                // 顶部文字公告最多 3 条，与 rbp2 首屏《顶部文字公告》行为一致
                                if (bannerAnnouncement.length >= 3) {
                                    GlobalNotification.error(
                                        i18n.t('first_screen_setting_page_max_three_notice')
                                    );
                                    return;
                                }
                                this.setState({
                                    bannerAnnouncementDetail: {},
                                    open: true,
                                    saveLoading: false,
                                });
                            }}
                        />
                    </Box>
                    <SubFunctionAuxiliaryHint enabled={auxiliaryModeEnabled} i18nKey="first_screen_aux_top_text_banner" />
                    {/* 表头行：与 Figma 一致为文案排布，非表格边框 */}
                    <Box
                        className="banner-announcement-layout__head-row"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            width: '100%',
                            typography: 'subtitle2',
                            fontWeight: 700,
                            color: 'var(--text-dark)',
                            fontSize: 16,
                            lineHeight: '24px',
                        }}
                    >
                        <Box sx={{ width: 48, flexShrink: 0, textAlign: 'center' }}>
                            {i18n.t('first_screen_setting_page_icon')}
                        </Box>
                        <Box sx={{ flex: '1 1 0%', minWidth: 0 }}>{i18n.t('first_screen_setting_page_notice_content')}</Box>
                        <Box sx={{ width: 80, flexShrink: 0, textAlign: 'center' }}>{i18n.t('general_text_operating')}</Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minHeight: 0, width: '100%' }}>
                        {bannerAnnouncement.length === 0 ? (
                            <Box
                                sx={{
                                    flex: '1 1 0',
                                    minHeight: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                }}
                            >
                                <Typography variant="body2" sx={{ color: 'var(--text-primary-500)' }}>
                                    {i18n.t('google_text_public_text_no_data')}
                                </Typography>
                            </Box>
                        ) : (
                            bannerAnnouncement.map((row, dataIndex) => {
                                const item = BANNER_ANNOUNCEMENT.find((it) => it.key === row.icon);
                                return (
                                    <Box
                                        key={row.id ?? dataIndex}
                                        className="banner-announcement-layout__data-row"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            width: '100%',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 48,
                                                flexShrink: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <span className="banner-announcement-option-icon">{item?.icon}</span>
                                        </Box>
                                        <Typography
                                            sx={{
                                                flex: '1 1 0%',
                                                minWidth: 0,
                                                fontSize: 16,
                                                fontWeight: 500,
                                                lineHeight: '24px',
                                                color: 'var(--text-dark)',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {row.value ?? ''}
                                        </Typography>
                                        <Box
                                            className="banner-announcement-layout__row-actions"
                                            sx={{
                                                width: 80,
                                                flexShrink: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 2,
                                            }}
                                        >
                                            <CardToolbarIconButton
                                                kind="edit"
                                                ariaLabel={i18n.t('general_text_edit')}
                                                onClick={() => {
                                                    const currenData = _.get(bannerAnnouncement, `[${dataIndex}]`, {});
                                                    this.setState({
                                                        bannerAnnouncementDetail: _.cloneDeep(currenData),
                                                        open: true,
                                                        saveLoading: false,
                                                    });
                                                }}
                                            />
                                            <CardToolbarIconButton
                                                kind="delete"
                                                ariaLabel={i18n.t('general_text_delete')}
                                                onClick={() => {
                                                    const currenData = _.get(
                                                        bannerAnnouncement,
                                                        `[${dataIndex}]`,
                                                        {} as BannerAnnouncement
                                                    );
                                                    if (currenData.id) {
                                                        this.setState({
                                                            deleteConfirmOpen: true,
                                                            deleteTargetId: currenData.id,
                                                            deleteHighlight: (currenData.value ?? '').trim() || '—',
                                                            deleteLoading: false,
                                                        });
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                </Box>

                <AlertDialog
                    open={deleteConfirmOpen}
                    variant="delete"
                    size="sm"
                    title={i18n.t('first_screen_setting_page_top_text_notify')}
                    bodyPrompt={i18n.t('alert_dialog_delete_confirm_question')}
                    highlightText={deleteHighlight}
                    confirmLoading={deleteLoading}
                    onCancel={this.closeDeleteConfirm}
                    onClose={this.closeDeleteConfirm}
                    onConfirm={() => {
                        void this.handleConfirmDeleteBanner();
                    }}
                />

                <DialogComponent
                    maxWidth="sm"
                    heightSize="small"
                    sx={getDialogFormBodyContentSx('start')}
                    dialogContentStyle={{ paddingTop: 0 }}
                    open={open}
                    onClose={() => {
                        this.setState({ open: false, saveLoading: false });
                    }}
                    defaultHeaderProps={{
                        title: i18n.t('first_screen_setting_page_top_text_notice_detail'),
                        onClose: () => {
                            this.setState({ open: false, saveLoading: false });
                        },
                    }}
                    footer={
                        <div>
                            <Button
                                color="primary"
                                variant="outlined"
                                disabled={saveLoading}
                                onClick={() => {
                                    this.setState({ open: false, saveLoading: false });
                                }}
                            >
                                {i18n.t('google_text_public_button_cancel')}
                            </Button>
                            <Box marginLeft={4} display="inline-block">
                                <LoadingOverlayButton
                                    color="primary"
                                    variant="contained"
                                    loading={saveLoading}
                                    loadingIndicatorSize={24}
                                    onClick={() => {
                                        void this.saveBannerAnnouncement();
                                    }}
                                >
                                    {i18n.t('google_text_confirm')}
                                </LoadingOverlayButton>
                            </Box>
                        </div>
                    }
                >
                    <DialogFormContentSurface fullHeight>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                width: '100%',
                            }}
                        >
                            {bannerAnnouncementForm.map((item) => (
                                <Box key={item.key}>
                                    <BaseInput
                                        required={item.require}
                                        regularType={item.regularType}
                                        variant="outlined"
                                        fullWidth
                                        label={item.label}
                                        value={_.get(bannerAnnouncementDetail, item.key) ?? ''}
                                        onChange={(event: any) => {
                                            this.onChangeBannerAnnouncement(event.target.value, item.key);
                                        }}
                                        select={item.select}
                                        items={item.items}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </DialogFormContentSurface>
                </DialogComponent>
            </Box>
        );
    }
}

type Props = {
    /** 云 RBP 子功能壳顶栏「辅助模式」是否开启 */
    auxiliaryModeEnabled?: boolean;
};

type State = {
    /** 顶部文字公告列表 */
    bannerAnnouncement: BannerAnnouncement[];
    bannerAnnouncementDetail?: BannerAnnouncement;
    open: boolean;
    /** 弹窗保存请求进行中 */
    saveLoading: boolean;
    /** 删除确认弹窗是否打开 */
    deleteConfirmOpen: boolean;
    /** 待删除项 id */
    deleteTargetId: number | null;
    /** 待删除项文案（弹窗高亮行） */
    deleteHighlight: string;
    /** 删除接口请求中 */
    deleteLoading: boolean;
};
